// Prompt Builder — Haiku Teaching Sessions
// ==========================================
// Constructs system prompts for Claude Haiku text-based teaching sessions.
// Replaces the Gemini-specific systemPrompt.ts for the new Haiku-powered sessions.
//
// The prompt is built from 3 layers:
//   1. Core persona (who Anna is)
//   2. Mode-specific instructions (how to behave in this session type)
//   3. Session context (what topic/subtopic, where the student is cognitively)

import i18n from '../i18n';

export interface PromptOptions {
  teachingForm: 'introduction' | 'dialog' | 'exam';
  topicTitle: string;
  subtopicTitle: string;
  /** JSON summary of current concept map nodes/edges */
  conceptMapState?: string;
  /** Current Bloom levels summary for this topic */
  bloomStatus?: string;
  /** Student's self-reported level and preferred pace */
  sessionFraming?: { level: string; pace: string };
  /** Exam format (e.g. 'oral', 'written') */
  examFormat?: string;
  /** Language override — defaults to current i18n language */
  language?: 'da' | 'en';
  /** Concept map editing rules summary for this teaching form */
  editingRules?: string;
}

/**
 * Build a system prompt for a Haiku-powered teaching session.
 *
 * Uses the `anna-haiku` i18n namespace so prompts can be maintained
 * separately from the legacy Gemini voice prompts in `anna`.
 */
export function buildHaikuSystemPrompt(options: PromptOptions): string {
  const {
    teachingForm,
    topicTitle,
    subtopicTitle,
    conceptMapState,
    bloomStatus,
    sessionFraming,
    examFormat,
    language,
    editingRules,
  } = options;

  // Resolve which i18n language to use for this prompt.
  // Language option lets callers pin to a specific language regardless of UI locale.
  const lng = language ?? i18n.language;
  const t = (key: string, vars?: Record<string, string>) =>
    i18n.t(key, { lng, ns: 'anna-haiku', ...vars });

  // Layer 1: Core persona — who Anna is
  const core = t('core');

  // Layer 2: Mode-specific instructions — how to behave in this session type
  const formInstructions = t(`forms.${teachingForm}`);

  // Layer 3: Session context — build incrementally, only include what's present
  const contextParts: string[] = [
    t('context.topic', { topic: topicTitle }),
    t('context.subtopic', { subtopic: subtopicTitle }),
  ];

  if (conceptMapState) {
    contextParts.push(t('context.conceptMap', { mapState: conceptMapState }));
  }

  if (bloomStatus) {
    contextParts.push(t('context.bloomStatus', { bloomStatus }));
  }

  if (teachingForm === 'exam' && examFormat) {
    contextParts.push(t('context.examFormat', { format: examFormat }));
  }

  if (sessionFraming) {
    contextParts.push(
      t('context.framing', { level: sessionFraming.level, pace: sessionFraming.pace })
    );
  }

  if (editingRules) {
    contextParts.push(editingRules);
  }

  const context = contextParts.join('\n');

  return `${core}\n\n${formInstructions}\n\n${context}`;
}
